import React from 'react';
import { Building2, Users, X } from 'lucide-react';

/**
 * What a team upload just told us about the company.
 *
 * The upload used to report per-row results and nothing else, so the moment
 * from the call — "this is the company, it has this many people, this
 * department has this many" — had nowhere to appear. This is that moment.
 *
 * It also says which dossier fields moved, because those landed as GUESSES: a
 * roster is strong evidence of headcount but still evidence (an upload may
 * cover one team), so the person is told there is something to confirm rather
 * than finding it silently changed.
 */

export interface RosterSummary {
  people: number;
  departments: number;
  per_department: { name: string; people: number }[];
  leadership: number;
  operational: number;
  level_unknown: number;
  dossier_fields_written?: number;
}

export interface RosterSummaryCardProps {
  summary: RosterSummary;
  onDismiss?: () => void;
}

export const RosterSummaryCard: React.FC<RosterSummaryCardProps> = ({ summary, onDismiss }) => {
  const { people, departments, per_department: perDept, leadership, operational, level_unknown: unknown } = summary;
  const wrote = summary.dossier_fields_written ?? 0;

  return (
    <section className="rounded-2xl bg-white p-5 ring-1 ring-gray-200/70">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-600" aria-hidden="true">
          <Users size={18} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold tracking-tight text-gray-900">
            That's {people} {people === 1 ? 'person' : 'people'} across{' '}
            {departments} {departments === 1 ? 'department' : 'departments'}.
          </h3>
          <p className="mt-0.5 text-[12.5px] text-gray-500">
            {leadership > 0 || operational > 0 ? (
              <>
                {leadership} leading, {operational} hands-on
                {unknown > 0 && <>, {unknown} whose level nobody has stated yet</>}.
              </>
            ) : (
              <>Nobody's seniority was in the file, so we can't split it yet.</>
            )}
          </p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-none rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Dismiss summary"
          >
            <X size={14} strokeWidth={2.25} />
          </button>
        )}
      </div>

      {perDept.length > 0 && (
        <ul className="mt-4 flex flex-col gap-1.5">
          {perDept.map(d => (
            <li
              key={d.name}
              className="flex items-baseline justify-between gap-3 rounded-lg bg-gray-50 px-3 py-1.5"
            >
              <span className="inline-flex min-w-0 items-center gap-2 text-[13px] font-medium text-gray-800">
                <Building2 size={12} strokeWidth={2.25} className="flex-none text-gray-400" />
                {d.name}
              </span>
              <span className="flex-none text-[12px] tabular-nums text-gray-500">
                {d.people} {d.people === 1 ? 'person' : 'people'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {wrote > 0 && (
        <p className="mt-3.5 text-[11.5px] leading-relaxed text-gray-400">
          We've pencilled your headcount and department count into your company
          profile from this. Both are marked as guesses until you confirm them.
        </p>
      )}
    </section>
  );
};

export default RosterSummaryCard;
